import { Bookmark, ShoppingBag, X } from "lucide-react";
import React, {
  useCallback,
  useLayoutEffect,
  useState,
  type RefObject,
} from "react";

import { useAppSelector } from "../../../Store/Hooks";
import { businessHealthColor } from "../../../Utils/Helper/businessHealth";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";

export type KpiSelectionAccount = {
  account_id: string;
  account_name: string;
  overallScore?: number;
};

export type SelectionBarDock = {
  left: number;
  width: number;
  bottom: number;
};

const initials = (name: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** Fixed at bottom of card; floats over scrolling content inside the panel. */
export const FLOATING_SELECTION_BAR_WRAPPER =
  "pointer-events-none absolute inset-x-4 bottom-4 z-20 sm:inset-x-5 sm:bottom-5";

/** Pinned footer inside card — always visible; chart stays above. */
export const FLOATING_SELECTION_BAR_FOOTER =
  "pointer-events-none z-20 shrink-0 bg-gradient-to-t from-white via-white/95 to-white/80 px-4 pb-3 pt-2 sm:pb-4";

export const FLOATING_SELECTION_BAR_PANEL =
  "pointer-events-auto flex w-full max-w-full items-center gap-2.5 rounded-2xl border border-slate-200/70 bg-white/95 px-2.5 py-2 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.28),0_8px_24px_-8px_rgba(91,33,182,0.18)] ring-1 ring-slate-200/60 backdrop-blur-md sm:gap-3 sm:px-3 sm:py-2.5";

const SAVE_WATCHLIST_BTN =
  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-violet-600 bg-white px-2 py-1.5 text-[11px] font-semibold leading-tight text-violet-700 transition-colors hover:bg-violet-50";

export type SelectionBarDockOptions = {
  /** Horizontal inset from bounds edges. Default 16. */
  insetX?: number;
  /** Gap from viewport/panel bottom. Default 12. */
  bottomGap?: number;
  /**
   * When true, always pin to the viewport bottom (ignore bounds bottom).
   * Use for tall scrollable lists so the bar does not sit on the last rows.
   */
  pinToViewportBottom?: boolean;
};

/** Syncs fixed bar position to panel bounds (Bird's Eye / Enterprise KPIs). */
export function useKpiSelectionBarDock(
  boundsRef: RefObject<HTMLElement | null>,
  active: boolean,
  options?: SelectionBarDockOptions
): SelectionBarDock | null {
  const [dock, setDock] = useState<SelectionBarDock | null>(null);
  const insetX = options?.insetX ?? 16;
  const bottomGap = options?.bottomGap ?? 12;
  const pinToViewportBottom = options?.pinToViewportBottom ?? false;

  const syncDock = useCallback(() => {
    const el = boundsRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDock({
      left: rect.left + insetX,
      width: Math.max(240, rect.width - insetX * 2),
      bottom: pinToViewportBottom
        ? bottomGap
        : Math.max(bottomGap, window.innerHeight - rect.bottom + bottomGap),
    });
  }, [boundsRef, insetX, bottomGap, pinToViewportBottom]);

  useLayoutEffect(() => {
    if (!active) {
      setDock(null);
      return;
    }
    syncDock();
    const el = boundsRef.current;
    const ro = new ResizeObserver(() => syncDock());
    if (el) ro.observe(el);
    window.addEventListener("resize", syncDock);
    window.addEventListener("scroll", syncDock, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncDock);
      window.removeEventListener("scroll", syncDock, true);
    };
  }, [active, syncDock, boundsRef]);

  return dock;
}

export interface KpiCompareSelectionStripProps {
  selectedAccounts: KpiSelectionAccount[];
  minCompareAccounts?: number;
  maxCompareAccounts?: number;
  canCompare: boolean;
  compareButtonTitle?: string;
  showSpiderViewHint?: boolean;
  hideCompareButton?: boolean;
  onCompare: () => void;
  onRemoveAccount: (accountId: string) => void;
  onClear: () => void;
  onSaveAsWatchlist?: () => void;
  /** When set, bar uses viewport-fixed positioning pinned to this panel (Bird's Eye style). */
  panelRef?: RefObject<HTMLElement | null>;
  /**
   * `overlay` (default): floats over content.
   * `footer`: in-flow below siblings (e.g. under pagination).
   */
  placement?: "overlay" | "footer";
}

export const KpiCompareSelectionStrip: React.FC<
  KpiCompareSelectionStripProps
> = ({
  selectedAccounts,
  minCompareAccounts = 2,
  maxCompareAccounts,
  canCompare,
  compareButtonTitle,
  showSpiderViewHint = true,
  hideCompareButton = false,
  onCompare,
  onRemoveAccount,
  onClear,
  onSaveAsWatchlist,
  panelRef,
  placement = "overlay",
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const dock = useKpiSelectionBarDock(
    panelRef ?? { current: null },
    Boolean(placement === "overlay" && panelRef && selectedAccounts.length > 0)
  );

  if (selectedAccounts.length === 0) return null;

  const panel = (
    <div className={FLOATING_SELECTION_BAR_PANEL}>
      <ShoppingBag
        className="h-5 w-5 shrink-0 text-slate-400"
        strokeWidth={1.75}
        aria-hidden
      />

      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {selectedAccounts.map(account => {
            const ringColor =
              businessHealthColor(
                Number.isFinite(account.overallScore)
                  ? (account.overallScore as number)
                  : 0,
                bhThresholds
              ) ?? "#ef4444";
            return (
              <button
                key={account.account_id}
                type="button"
                onClick={() => onRemoveAccount(account.account_id)}
                className="group relative shrink-0"
                title={`Remove ${account.account_name} from selection`}
                aria-label={`Remove ${account.account_name} from selection`}
              >
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white text-[10px] font-bold leading-none text-slate-700 sm:h-10 sm:w-10 sm:text-[11px]"
                  style={{ borderColor: ringColor }}
                >
                  {initials(account.account_name)}
                </span>
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white shadow ring-2 ring-white transition-opacity group-hover:bg-rose-600">
                  <X className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
              </button>
            );
          })}
      </div>

      {showSpiderViewHint ? (
        <div className="hidden shrink-0 rounded-full border border-amber-300/90 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 shadow-sm sm:inline-flex">
          {maxCompareAccounts
            ? `Spider View min ${minCompareAccounts} · max ${maxCompareAccounts}`
            : `Spider View min ${minCompareAccounts}`}
        </div>
      ) : null}

      <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
        {onSaveAsWatchlist ? (
          <button
            type="button"
            onClick={onSaveAsWatchlist}
            className={SAVE_WATCHLIST_BTN}
            title="Save selected accounts as a watchlist"
          >
            <Bookmark className="h-3 w-3 shrink-0" strokeWidth={2} />
            Save as watchlist
          </button>
        ) : null}
        {!hideCompareButton ? (
          <button
            type="button"
            disabled={!canCompare}
            title={compareButtonTitle}
            onClick={onCompare}
            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-violet-600 px-2 py-1.5 text-[11px] font-semibold leading-tight text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {maxCompareAccounts
              ? `Compare (${Math.min(selectedAccounts.length, maxCompareAccounts)}) · ${maxCompareAccounts} max`
              : `Compare (${selectedAccounts.length})`}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClear}
          className="px-1 py-0.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:underline"
        >
          Clear
        </button>
      </div>
    </div>
  );

  if (placement === "footer") {
    return <div className={FLOATING_SELECTION_BAR_FOOTER}>{panel}</div>;
  }

  if (panelRef && dock) {
    return (
      <div
        className="pointer-events-none fixed z-[80]"
        style={{
          left: dock.left,
          width: dock.width,
          bottom: dock.bottom,
        }}
      >
        {panel}
      </div>
    );
  }

  return <div className={FLOATING_SELECTION_BAR_WRAPPER}>{panel}</div>;
};

export default KpiCompareSelectionStrip;
